import { tartApiGet as get, tartApiPost as post } from '@/utils/ajax'
import { sleep } from '.'

const API_HOST = window.CONFIG.tart_api

const CANCEL_RUN_EVENT_MAP = {}

export default class TartIframeApi {
  constructor(iframe, origin) {
    this.iframe = iframe
    this.origin = origin
    this.eventsMap = {
      IFRAME_READY: [],
      WORK_CURRENT_CONTENT_AND_INPUT: [],
      WORK_CURRENT_CONTENT: [],
      WORK_CURRENT_INPUT: []
    }

    window.addEventListener('message', (event) => {
      const msgType = event.data.type
      const eventsList = this.eventsMap[msgType]
      if (!eventsList) return
      eventsList.forEach((cb) => cb(event.data.payload))
    })
  }

  /**
   *
   * @param {() => void} cb
   * @returns
   */
  onIframeReady(cb) {
    this.eventsMap.IFRAME_READY.push(cb)
    return () => {
      this.eventsMap.IFRAME_READY = this.eventsMap.IFRAME_READY.filter((c) => c !== cb)
    }
  }

  /**
   *
   * @param {(params: {content: string, input: string}) => void} cb
   * @returns
   */
  onWorkCurrentContentAndInput(cb) {
    this.eventsMap.WORK_CURRENT_CONTENT_AND_INPUT.push(cb)
    return () => {
      this.eventsMap.WORK_CURRENT_CONTENT_AND_INPUT = this.eventsMap.WORK_CURRENT_CONTENT_AND_INPUT.filter(
        (c) => c !== cb
      )
    }
  }

  /**
   *
   * @param {(params: {content: string}) => void} cb
   * @returns
   */
  onWorkCurrentContent(cb) {
    this.eventsMap.WORK_CURRENT_CONTENT.push(cb)
    return () => {
      this.eventsMap.WORK_CURRENT_CONTENT = this.eventsMap.WORK_CURRENT_CONTENT.filter((c) => c !== cb)
    }
  }

  /**
   *
   * @param {(params: {input: string}) => void} cb
   * @returns
   */
  onWorkCurrentInput(cb) {
    this.eventsMap.WORK_CURRENT_INPUT.push(cb)
    return () => {
      this.eventsMap.WORK_CURRENT_INPUT = this.eventsMap.WORK_CURRENT_INPUT.filter((c) => c !== cb)
    }
  }

  /**
   *
   * @param {{token: string, origin: string}} options
   */
  setToken(options) {
    const { token, origin } = options
    this.iframe.postMessage(
      {
        type: 'AUTH_SET_TOKEN',
        payload: {
          token,
          origin
        }
      },
      this.origin
    )
  }

  /**
   *
   * @param {boolean} isEditable
   */
  setEditorEditable(isEditable = true) {
    this.iframe.postMessage(
      {
        type: 'EDITOR_SET_EDITABLE',
        payload: {
          editable: isEditable
        }
      },
      this.origin
    )
  }

  /**
   *
   * @returns {Promise<{content: string, input: string}>}
   */
  getContentAndInput() {
    return new Promise((resolve, reject) => {
      const offLisen = this.onWorkCurrentContentAndInput((payload) => {
        offLisen()
        resolve(payload)
      })
      this.iframe.postMessage(
        {
          type: 'WORK_GET_CURRENT_CONTENT_AND_INPUT'
        },
        this.origin
      )
      setTimeout(() => {
        offLisen()
        reject(new Error('获取内容和输入超时'))
      }, 10000)
    })
  }

  /**
   *
   * @returns {Promise<{content: string}>}
   */
  getContent() {
    return new Promise((resolve, reject) => {
      const offLisen = this.onWorkCurrentContent((payload) => {
        offLisen()
        resolve(payload)
      })
      this.iframe.postMessage(
        {
          type: 'WORK_GET_CURRENT_CONTENT'
        },
        this.origin
      )
      setTimeout(() => {
        offLisen()
        reject(new Error('获取内容超时'))
      }, 10000)
    })
  }

  /**
   *
   * @returns {Promise<{input: string}>}
   */
  getInput() {
    return new Promise((resolve, reject) => {
      const offLisen = this.onWorkCurrentInput((payload) => {
        offLisen()
        resolve(payload)
      })
      this.iframe.postMessage(
        {
          type: 'WORK_GET_CURRENT_INPUT'
        },
        this.origin
      )
      setTimeout(() => {
        offLisen()
        reject(new Error('获取输入超时'))
      }, 10000)
    })
  }

  /**
   *
   * @param {{content: string}} options
   */
  setCurrentContent(options) {
    const { content } = options
    this.iframe.postMessage(
      {
        type: 'WORK_SET_CURRENT_CONTENT',
        payload: {
          content
        }
      },
      this.origin
    )
  }

  /**
   *
   * @param {{input: string}} options
   */
  setCurrentInput(options) {
    const { input } = options
    this.iframe.postMessage(
      {
        type: 'WORK_SET_CURRENT_INPUT',
        payload: {
          input
        }
      },
      this.origin
    )
  }

  /**
   *
   * @param {{content: string, input: string}} options
   */
  setCurrentContentAndInput(options) {
    const { content, input } = options
    this.iframe.postMessage(
      {
        type: 'WORK_SET_CURRENT_CONTENT_AND_INPUT',
        payload: {
          content,
          input
        }
      },
      this.origin
    )
  }

  /**
   * 用例执行状态，SUCCESS运行成功 TLE超时 MLE超出内存 OLE超出输出 RE运行错误 SE系统错误
   * @param {{submission_id: string}} options
   * @returns {Promise<{
   *  state: 'SUCCESS' | 'RUNNING' | 'COMPILING' | 'PENDING' | 'FAILED'
   *  compile_state: 'SUCCESS' | 'FAILED'
   *  compile_output: string
   *  run_result: {
   *    input: string
   *    output: string
   *    run_status: 'SUCCESS' | 'TLE' | 'MLE' | 'RE' | 'OLE' | 'CE' | 'SE'
   *    time_expand: number
   *    memory_expand: number
   *  }[]
   * }>}
   */
  static async getSubmissionResult(options) {
    const { submission_id } = options
    return get(`${API_HOST}/submissions/${submission_id}/caseResult`)
  }

  /**
   *
   * @param {{
   *  language: string,
   *  code: string,
   *  time_limit: string,
   *  memory_limit: string,
   *  output_limit: string,
   *  case_list: {
   *    input: string
   *  }[]
   *  submissionIdCallback?: (submissionId: string) => void
   * }} options
   *
   * @returns {Promise<{
   *  result: {
   *    state: 'SUCCESS' | 'RUNNING' | 'COMPILING' | 'PENDING' | 'FAILED'
   *    compile_state: 'SUCCESS' | 'FAILED'
   *    compile_output: string
   *    run_result: {
   *     input: string
   *     output: string
   *     run_status: 'SUCCESS' | 'TLE' | 'MLE' | 'RE' | 'OLE' | 'CE' | 'SE'
   *     time_expand: number
   *     memory_expand: number
   *  }[]
   * },
   *  submission_id: string
   *  isInterrupted?: boolean
   *  isTimeout?: boolean
   * }>}
   */
  static async runMultiCases(options) {
    const {
      language,
      code,
      time_limit = 30000,
      memory_limit,
      output_limit = 500000,
      case_list,
      submissionIdCallback
    } = options

    const data = await post(
      `${API_HOST}/submissions/runCases`,
      {
        language,
        code,
        time_limit,
        memory_limit,
        output_limit,
        case_list
      },
      {
        showErrMsg: false
      }
    )
    const { submission_id } = data
    submissionIdCallback && submissionIdCallback(submission_id)
    return new Promise((resolve, reject) => {
      let isTimeout = false
      const timeoutTimer = setTimeout(() => {
        clearTimeout(timeoutTimer)
        isTimeout = true
        resolve({ submission_id, result: null, isTimeout: true })
      }, 30000)

      let isCanceled = false
      this.onCancelRunEvent(submission_id, () => {
        isCanceled = true
        clearTimeout(timeoutTimer)
        resolve({ submission_id, result: null, isInterrupted: true })
      })

      const _getSubmissionResult = async () => {
        this.getSubmissionResult({ submission_id })
          .then(async (result) => {
            if (result.state === 'SUCCESS') {
              clearTimeout(timeoutTimer)
              resolve({ submission_id, result })
            } else if (result.state === 'INTERRUPT') {
              clearTimeout(timeoutTimer)
              resolve({ submission_id, result, isInterrupted: true })
            } else if (result.state === 'FAILED') {
              reject(new Error(result.compile_output))
            } else {
              if (isTimeout || isCanceled) {
                return
              }
              await sleep(5000)
              _getSubmissionResult()
            }
          })
          .catch((e) => {
            reject(new Error(e))
          })
      }
      _getSubmissionResult()
    })
  }

  /**
   *
   * @param {string} submissionId
   * @returns {Promise<{
   *  state: 'SUCCESS'
   * }>}
   */
  static async cancelRun(submissionId) {
    const res = await post(`${API_HOST}/submissions/${submissionId}/stop`)
    this.emitCancelRunEvent(submissionId, res)
    return res
  }

  static onCancelRunEvent(submissionId, cb) {
    const eventsList = CANCEL_RUN_EVENT_MAP[submissionId] || []
    eventsList.push(cb)
    return () => {
      CANCEL_RUN_EVENT_MAP[submissionId] = CANCEL_RUN_EVENT_MAP[submissionId].filter(
        (c) => c !== cb
      )
    }
  }

  static emitCancelRunEvent(submissionId, payload) {
    const eventsList = CANCEL_RUN_EVENT_MAP[submissionId] || []
    eventsList.forEach((cb) => cb(payload))
    delete CANCEL_RUN_EVENT_MAP[submissionId]
  }
}
